import { HStack, VStack } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Card } from '@televet/kibble-ui/build/components/Card';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { formatNames } from 'pages/Conversations/components/ChannelView/MessageComposer/Toolbar/utils';
import { PetParent } from 'pages/Tasks/types';
import React, { useCallback, useMemo } from 'react';
import { PetParentAvatar } from 'shared/components/Avatars';
import { useSidePanelSearch } from 'shared/components/SidePanel/components/SearchPanel/state/providers/SearchPanelProvider';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';

interface PetParentSearchResultCardProps {
  petParent: PetParent;
  onDelete?: () => void;
  onSelect?: (petParent: PetParent) => void;
}

const PetParentSearchResultCard = ({ petParent, onSelect, onDelete }: PetParentSearchResultCardProps): JSX.Element => {
  const { viewClinicPetParent, setCurrentClinicPetId } = useSidePanelSearch();
  const { isClientIdSupported } = useIntegrationsProvider();

  const handleCardClick = useCallback(() => {
    onSelect?.(petParent);
  }, [onSelect, petParent]);

  const onDeleteClick = useCallback(() => {
    onDelete?.();
  }, [onDelete]);

  const petNames = useMemo(() => {
    return formatNames((petParent.pets || []).map((pet) => pet.name));
  }, [petParent.pets]);

  return (
    <Card size="sm" onClick={handleCardClick} isInteractive={!!onSelect} w="100%">
      <HStack className={`PetParentSearch__${onDelete ? 'Selection' : 'Result'}`} w="100%" justify="space-between">
        <HStack>
          <PetParentAvatar
            size="lg"
            clinicPetParent={petParent}
            onClick={(): void => {
              setCurrentClinicPetId('');
              viewClinicPetParent({
                clinicPetParentId: petParent.id || '',
                isPanelZIndexIncreased: true,
                clearConversationId: true,
              });
            }}
          />
          <VStack spacing={0} align="flex-start">
            <Heading size="sm">
              {petParent.firstName} {petParent.lastName}
              {!petParent?.isActive && ' (Inactive)'}
              {isClientIdSupported && petParent?.pimsId && <Text size="xs">{` - ID: ${petParent.pimsId}`}</Text>}
            </Heading>
            <Text as="p" size="sm">
              {petNames || 'No Patients'}
            </Text>
          </VStack>
        </HStack>
        {!!onDelete && <Button variant="ghostNeutral" iconName="trashcan" onClick={onDeleteClick} />}
      </HStack>
    </Card>
  );
};

export default PetParentSearchResultCard;
